<template>
  <!-- How much room a server has: CPU load (with the last hour), free memory,
       free disk. For choosing where an app runs. -->
  <div class="kvm-res" :aria-label="'Resources on ' + (server.name || 'this server')" role="group">
    <div class="kvm-res__item">
      <div class="kvm-res__label">CPU</div>
      <div class="kvm-res__value">
        <template v-if="cpu != null">{{ cpu }}% <span class="kvm-res__muted">load</span></template>
        <template v-else>—</template>
      </div>
      <kvm-sparkline v-if="cpuSeries.length" :value="cpuSeries" :height="22" :line-width="1.5" :padding="2" :max-points="30"
                     :color="cpuBusy ? lowColor : okColor" class="kvm-res__spark" />
    </div>

    <div class="kvm-res__item">
      <div class="kvm-res__label">Memory</div>
      <div class="kvm-res__value">
        <template v-if="ramFree != null">{{ kvmSize(ramFree) }} <span class="kvm-res__muted">free of {{ kvmSize(server.ram.total) }}</span></template>
        <template v-else>—</template>
      </div>
      <v-progress-linear v-if="ramUsedPct != null" :value="ramUsedPct" :color="barColor(ramUsedPct)" height="5" rounded
                         background-color="grey lighten-2" class="kvm-res__bar" aria-hidden="true" />
    </div>

    <div class="kvm-res__item">
      <div class="kvm-res__label">Disk</div>
      <div class="kvm-res__value">
        <template v-if="disk">{{ kvmSize(disk.free) }} <span class="kvm-res__muted">free of {{ kvmSize(disk.total) }}</span></template>
        <template v-else-if="diskLoading"><span class="kvm-res__muted">Checking…</span></template>
        <template v-else>—</template>
      </div>
      <v-progress-linear v-if="diskUsedPct != null" :value="diskUsedPct" :color="barColor(diskUsedPct)" height="5" rounded
                         background-color="grey lighten-2" class="kvm-res__bar" aria-hidden="true" />
    </div>

    <p v-if="lowNote" class="kvm-res__note" role="note">{{ lowNote }}</p>
  </div>
</template>

<script>
import kvm from '@/api/kvm'
import { kvmSize } from '@/views/services/hosting/kvm/kvmFormat'
import KvmSparkline from '@/views/services/hosting/kvm/KvmSparkline'

// Used share at which a resource is getting tight / nearly gone.
const TIGHT = 80
const FULL = 90

/**
 * A server's headroom at a glance. `server` is a dockerServerInfo() row
 * ({ id, name, cpu, ram: { total, used }, metricsHistory }); its disk (guest
 * agent) and last hour of CPU are fetched here, so nothing waits on them.
 */
export default {
  name: 'KvmServerResources',
  components: { KvmSparkline },
  props: {
    server: { type: Object, required: true },
  },
  data: () => ({
    disk: null,
    diskLoading: false,
    metrics: null,
    okColor: '#607d8b', // blue-grey: fine (not brand green)
    lowColor: '#ef6c00', // orange: tight
  }),
  computed: {
    cpu () {
      return this.server.cpu != null ? Math.round(this.server.cpu) : null
    },
    cpuSeries () {
      return ((this.metrics && this.metrics.cpu) || []).map(p => (p[1] == null ? 0 : p[1] * 100))
    },
    // Busy over the last hour (or right now, without history).
    cpuBusy () {
      const s = this.cpuSeries
      const avg = s.length ? s.reduce((a, b) => a + b, 0) / s.length : this.cpu
      return avg != null && avg >= TIGHT
    },
    ramFree () {
      const r = this.server.ram || {}
      return r.total ? Math.max(0, r.total - (r.used || 0)) : null
    },
    ramUsedPct () {
      const r = this.server.ram || {}
      return r.total && r.used != null ? Math.round(r.used / r.total * 100) : null
    },
    diskUsedPct () {
      return this.disk && this.disk.total ? Math.round(this.disk.used / this.disk.total * 100) : null
    },
    lowNote () {
      const low = []
      if (this.diskUsedPct != null && this.diskUsedPct >= FULL) low.push('disk space')
      if (this.ramUsedPct != null && this.ramUsedPct >= FULL) low.push('memory')
      if (this.cpuBusy) low.push('CPU (it’s been busy)')
      return low.length ? `Low on ${low.join(' and ')} — a new app may run slowly here.` : null
    },
  },
  watch: {
    'server.id': { immediate: true, handler () { this.load() } },
  },
  methods: {
    kvmSize,
    barColor (usedPct) {
      if (usedPct >= FULL) return 'error'
      if (usedPct >= TIGHT) return 'warning'
      return 'blue-grey'
    },
    load () {
      if (!this.server.id) return
      this.diskLoading = true
      kvm.getDisk(this.server.id)
        .then(d => { this.disk = d })
        .catch(() => { this.disk = null }) // agent not answering — just not shown
        .finally(() => { this.diskLoading = false })
      if (this.server.metricsHistory) {
        kvm.getMetrics(this.server.id, '1h')
          .then(m => { this.metrics = m })
          .catch(() => { this.metrics = null })
      }
    },
  },
}
</script>

<style scoped lang="scss">
@import './kvm-ui';

.kvm-res {
  display: flex;
  flex-wrap: wrap;
  gap: 8px 20px;
  margin-top: 6px;
}
.kvm-res__item {
  min-width: 120px;
  flex: 1 1 120px;
  max-width: 200px;
}
.kvm-res__label {
  @include kvm-secondary-text;
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}
.kvm-res__value { font-size: 0.82rem; font-weight: 500; }
.kvm-res__muted { @include kvm-secondary-text; font-weight: 400; }
.kvm-res__bar { margin-top: 4px; }
.kvm-res__spark { margin-top: 2px; }
.kvm-res__note {
  flex-basis: 100%;
  margin: 0;
  font-size: 0.75rem;
  color: #b45309;
  .theme--dark & { color: #fbbf24; }
}
</style>
